import { useCallback, useEffect, useRef, useState } from "react";
import imagePickerHelper from "@utils/imagePickerHelper";

// Promise wrapper around the existing callback-style imagePickerHelper.
// Resolves with the uploaded URL on success, null on cancel/error.
//
// Used by MarkingSheet for damaged / wrong-item evidence photos. The
// underlying helper:
//   1. shows a Camera/Gallery alert
//   2. picks a photo (compressed to 0.4 quality, max-height 2048)
//   3. POSTs to media/upload-image
//   4. fires onSuccessCallback({ url })

const useUploadImageFromPicker = () => {
  const [uploading, setUploading] = useState(false);
  const isMounted = useRef(true);

  useEffect(
    () => () => {
      isMounted.current = false;
    },
    [],
  );

  const pick = useCallback(
    () =>
      new Promise((resolve) => {
        let started = false;
        let resolved = false;
        // 60s safety net — covers iOS users backgrounding the app between the
        // Camera/Gallery alert and the native picker, plus the imagePicker
        // helper's silent-on-network-error path. Without this, a botched
        // upload leaves the consumer's spinner spinning forever.
        const safety = setTimeout(() => {
          settle(null);
        }, 60_000);
        const settle = (value) => {
          if (resolved) return;
          resolved = true;
          clearTimeout(safety);
          if (isMounted.current) setUploading(false);
          resolve(value);
        };
        imagePickerHelper({
          onSelectCallback: (response) => {
            if (response?.didCancel || response?.errorCode) {
              settle(null);
              return;
            }
            if (response?.assets?.[0]) {
              started = true;
              if (isMounted.current) setUploading(true);
            }
          },
          onSuccessCallback: (uploadData) => {
            const url =
              uploadData?.url ||
              uploadData?.data?.url ||
              uploadData?.data?.image_url ||
              null;
            settle(url);
          },
          onDismiss: () => {
            // Android-only — fired when the Alert is dismissed by tapping
            // outside before any picker is opened.
            if (!started) settle(null);
          },
        });
      }),
    [],
  );

  return { pick, uploading };
};

export default useUploadImageFromPicker;
